(() => {
  if (customElements.get('maxler-protein-flavours')) return;
  const node = (tag, cls, text) => {
    const element = document.createElement(tag);
    if (cls) element.className = cls;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  class ProteinFlavours extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      const signal = this.signal;
      const ready = () => queueMicrotask(() => { if (!signal.aborted) this.start(); });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, {once: true, signal});
      else ready();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.announcement?.remove();
      this.initialized = false;
      this.main = null;
      this.items = null;
      delete this.dataset.enhanced;
    }
    start() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-pf-labels]').textContent);
      this.tiles = this.querySelector('[data-pf-tiles]');
      this.error = this.querySelector('[data-pf-error]');
      this.querySelectorAll('[data-pf-announcement]').forEach(element => element.remove());
      this.announcement = node('p', 'visually-hidden');
      this.announcement.dataset.pfAnnouncement = '';
      this.announcement.setAttribute('aria-live', 'polite');
      this.announcement.setAttribute('aria-atomic', 'true');
      this.append(this.announcement);
      this.addEventListener('click', event => this.click(event), {signal: this.signal});
      document.addEventListener('variantChange', event => {
        if (event.target.closest?.('maxler-protein-product') !== this.main || !Object.hasOwn(event.detail || {}, 'variant')) return;
        const signal = this.signal;
        queueMicrotask(() => { if (!signal.aborted) this.refresh(event.detail.variant); });
      }, {capture: true, signal: this.signal});
      document.addEventListener('shopify:section:load', () => {
        const signal = this.signal;
        queueMicrotask(() => { if (!signal.aborted) this.attach(); });
      }, {signal: this.signal});
      this.attach();
    }
    attach() {
      const main = [...document.querySelectorAll('maxler-protein-product')].find(element => element.dataset.productId === this.dataset.productId);
      if (!main || main.dataset.enhanced !== 'true' || !main.flavourSelect) return;
      if (main === this.main && this.items) { this.refresh(main.current, false); return; }
      this.main = main;
      this.flavourIndex = main.selects.indexOf(main.flavourSelect);
      const library = JSON.parse(main.querySelector('[data-protein-flavour-library]')?.textContent || '[]');
      this.rows = [...new Map(library.filter(row => [...main.flavourSelect.options].some(option => String(option.dataset.optionValueId) === String(row.id)) && main.flavourVariants(row.nativeValue).length).map(row => [String(row.id), row])).values()].sort((a, b) => a.position - b.position);
      this.map = new Map(this.rows.map(row => [String(row.id), row]));
      if (!this.rows.length) return;
      const focused = document.activeElement?.closest?.('[data-pf-select]')?.dataset.pfSelect;
      this.items = new Map();
      this.tiles.replaceChildren();
      for (const row of this.rows) {
        const li = node('li', 'protein-flavours__tile');
        const select = node('button', 'protein-flavours__tile-control');
        const media = node('span', 'protein-flavours__tile-media');
        const caption = node('span', 'protein-flavours__tile-caption');
        const name = node('strong', 'protein-flavours__tile-label', row.name);
        const status = node('small', 'protein-flavours__tile-status');
        const check = node('span', 'protein-flavours__check', '✓');
        li.dataset.pfItem = row.id;
        select.type = 'button';
        select.dataset.pfSelect = row.id;
        select.dataset.pfTile = row.id;
        name.id = 'ProteinFlavourName-' + this.dataset.sectionId + '-' + row.id;
        status.id = 'ProteinFlavourStatus-' + this.dataset.sectionId + '-' + row.id;
        status.dataset.pfStatus = '';
        select.setAttribute('aria-labelledby', name.id + ' ' + status.id);
        check.setAttribute('aria-hidden', 'true');
        caption.append(name, check, document.createTextNode(' '), status);
        select.append(media, caption);
        li.append(select);
        this.items.set(String(row.id), {li, select, media, status});
      }
      this.more = node('li', 'protein-flavours__tile protein-flavours__more');
      this.more.dataset.pfMore = '';
      this.toggle = node('button', 'protein-flavours__tile-control protein-flavours__toggle');
      this.toggle.type = 'button';
      this.toggle.dataset.pfToggle = '';
      this.toggle.setAttribute('aria-controls', this.tiles.id);
      const icon = node('span', 'protein-flavours__tile-media protein-flavours__more-visual');
      this.moreIcon = node('span', '', '+');
      this.moreIcon.setAttribute('aria-hidden', 'true');
      icon.append(this.moreIcon);
      const caption = node('span', 'protein-flavours__tile-caption');
      this.moreTitle = node('strong', 'protein-flavours__tile-label');
      this.moreCount = node('small', 'protein-flavours__tile-status');
      caption.append(this.moreTitle, this.moreCount);
      this.toggle.append(icon, caption);
      this.more.append(this.toggle);
      this.querySelector('[data-pf-count]').textContent = (this.rows.length === 1 ? this.labels.countOne : this.labels.count).replaceAll('[count]', String(this.rows.length));
      this.contextKey = null;
      this.selectedId = null;
      this.expanded = false;
      this.dataset.enhanced = 'true';
      this.refresh(main.current, false);
      if (focused && this.visibleIds.has(focused)) this.items.get(focused)?.select.focus({preventScroll: true});
    }
    variants(row) { return this.main.flavourVariants(row.nativeValue); }
    exact(row) { return this.main.combination(this.flavourIndex, row.nativeValue); }
    canBuy(variant) { return this.main.sizeAvailable(variant); }
    state(row) {
      const variants = this.variants(row), exact = this.exact(row), available = variants.filter(variant => this.canBuy(variant));
      let kind, rank;
      if (exact && this.canBuy(exact)) { kind = 'available'; rank = 0; }
      else if (available.length) { kind = 'otherSizes'; rank = 1; }
      else if (variants.some(variant => variant.available)) { kind = 'restricted'; rank = 3; }
      else { kind = 'soldOut'; rank = 2; }
      return {kind, rank, exact, available, label: this.labels[kind === 'restricted' ? 'marketRestricted' : kind]};
    }
    rowForVariant(variant) {
      const value = variant?.options[this.flavourIndex];
      const option = [...this.main.flavourSelect.options].find(option => option.value === value);
      return this.map.get(String(option?.dataset.optionValueId));
    }
    refresh(variant, announce = true) {
      const row = this.rowForVariant(variant);
      if (!row) return;
      const previous = this.selectedId;
      const key = JSON.stringify([this.main.dataset.country, this.main.dataset.us, ...variant.options.filter((_, index) => index !== this.flavourIndex)]);
      if (this.contextKey !== null && this.contextKey !== key) this.expanded = false;
      this.contextKey = key;
      this.selectedId = String(row.id);
      const displayRank = row => this.state(row).available.length ? 0 : this.state(row).rank;
      this.order = [...this.rows].sort((a, b) => displayRank(a) - displayRank(b) || a.position - b.position);
      for (const current of this.rows) {
        const item = this.items.get(String(current.id)), state = this.state(current), selected = String(current.id) === this.selectedId;
        item.li.classList.toggle('is-selected', selected);
        item.select.setAttribute('aria-pressed', String(selected));
        item.status.textContent = state.kind === 'available' ? '' : state.kind === 'otherSizes' ? this.labels.otherSizesShort : state.label;
        item.status.hidden = !item.status.textContent;
        item.li.dataset.pfState = state.kind;
        item.li.dataset.pfCombination = state.exact ? 'exists' : 'missing';
      }
      this.visibility();
      if (previous !== this.selectedId && announce) this.announcement.textContent = [row.name, this.package(variant), this.state(row).label].filter(Boolean).join(' · ');
    }
    image(row, item) {
      const photo = row.image;
      if (item.imageSource === (photo?.src || '')) return;
      item.imageSource = photo?.src || '';
      item.media.replaceChildren();
      if (!photo) return;
      const img = document.createElement('img');
      img.alt = photo.alt || '';
      img.width = 720;
      img.height = 720;
      img.decoding = 'async';
      img.loading = 'lazy';
      img.sizes = '(min-width:1440px) 322px, (min-width:1024px) calc((100vw - 152px) / 4), (min-width:768px) calc((100vw - 96px) / 3), calc((100vw - 44px) / 2)';
      img.srcset = photo.srcset || '';
      img.src = photo.src;
      img.style.objectPosition = photo.focal || '50% 50%';
      item.media.dataset.imageKind = photo.kind || 'flavour';
      item.media.append(img);
    }
    package(variant) { return variant?.options.filter((_, index) => index !== this.flavourIndex).join(' / ') || ''; }
    visibility() {
      if (!this.items || !this.order) return;
      const available = this.order.filter(row => this.state(row).available.length > 0);
      const remaining = this.order.filter(row => this.state(row).available.length === 0);
      const visible = this.expanded ? this.order : available;
      this.visibleIds = new Set(visible.map(row => String(row.id)));
      this.toggle.setAttribute('aria-expanded', String(this.expanded));
      this.moreTitle.textContent = this.expanded ? this.labels.showFewer : this.labels.moreTile;
      this.moreCount.textContent = (this.expanded ? this.labels.count : this.labels.moreCount).replaceAll('[count]', String(this.expanded ? this.rows.length : remaining.length));
      this.moreIcon.textContent = this.expanded ? '−' : '+';
      this.more.hidden = remaining.length === 0;
      this.dataset.availableCount = String(available.length);
      this.dataset.remainingCount = String(remaining.length);
      const focused = document.activeElement;
      for (const row of this.order) {
        const item = this.items.get(String(row.id));
        if (!this.visibleIds.has(String(row.id)) && item.li.contains(focused) && !this.more.hidden) this.toggle.focus({preventScroll: true});
        item.li.hidden = !this.visibleIds.has(String(row.id));
        if (!item.li.hidden) this.image(row, item);
      }
      const elements = [...available.map(row => this.items.get(String(row.id)).li), this.more, ...remaining.map(row => this.items.get(String(row.id)).li)];
      elements.forEach((element, index) => { if (this.tiles.children[index] !== element) this.tiles.insertBefore(element, this.tiles.children[index] || null); });
      if (this.contains(focused) && focused.getClientRects().length) focused.focus({preventScroll: true});
    }
    message(text = '') { this.error.textContent = text; this.error.hidden = !text; }
    commit(id, opener) {
      const previous = this.main.current;
      this.message();
      try {
        if (!this.main.variants.some(variant => String(variant.id) === String(id))) throw Error('Unknown variant');
        this.main.selectVariant(id, {focus: false});
        if (String(this.main.current?.id) !== String(id)) throw Error('Variant was not confirmed');
        this.main.renderFlavourNotice?.();
        this.refresh(this.main.current);
        const returnTo = opener?.isConnected && opener.getClientRects().length ? opener : this.items.get(this.selectedId)?.select;
        if (returnTo?.getClientRects().length) returnTo.focus({preventScroll: true});
        else this.toggle.focus({preventScroll: true});
      } catch {
        if (previous && String(this.main.current?.id) !== String(previous.id)) { try { this.main.selectVariant(previous.id, {focus: false}); } catch {} }
        this.refresh(this.main.current || previous, false);
        this.message(this.labels.changeError);
      }
    }
    choose(row, opener) {
      const state = this.state(row);
      const target = state.exact && this.canBuy(state.exact) ? state.exact : state.available[0] || state.exact || this.variants(row)[0];
      if (!target) return;
      if (this.package(target) !== this.package(this.main.current)) {
        const text = this.labels.sizeNotice.replaceAll('[flavour]', row.name).replaceAll('[previous_size]', this.package(this.main.current)).replaceAll('[size]', this.package(target));
        this.main.flavourSizeNotice = {variantId: String(target.id), text};
      } else this.main.flavourSizeNotice = null;
      this.commit(target.id, opener);
    }
    click(event) {
      const target = event.target.closest('[data-pf-select],[data-pf-toggle]');
      if (!target || !this.contains(target) || this.dataset.enhanced !== 'true' || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      event.preventDefault();
      this.message();
      if (target.hasAttribute('data-pf-toggle')) { this.expanded = !this.expanded; this.visibility(); }
      else {
        const row = this.map.get(String(target.dataset.pfSelect));
        if (row) this.choose(row, target);
      }
    }
  }
  customElements.define('maxler-protein-flavours', ProteinFlavours);
})();
